import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Avatar,
  Paper,
  Divider,
  Skeleton,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PsychologyIcon from '@mui/icons-material/Psychology';
import GroupsIcon from '@mui/icons-material/Groups';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useImageLoad from '../hooks/useImageLoad';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const heroImageLoaded = useImageLoad('/images/hero-medical.svg');

  const beneficios = [
    {
      icon: <HealthAndSafetyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'Atención Integral',
      descripcion: 'Cuidamos tu salud física, mental y social con un enfoque personalizado'
    },
    {
      icon: <PsychologyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'Apoyo Continuo',
      descripcion: 'Te acompañamos en cada paso, brindándote la tranquilidad que necesitas'
    },
    {
      icon: <SmartToyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'IA Avanzada',
      descripcion: 'Tecnología de última generación para un análisis preciso y confiable'
    },
    {
      icon: <GroupsIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'Comunidad Activa',
      descripcion: 'Únete a una red de apoyo comprometida con tu bienestar'
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'Siempre Disponible',
      descripcion: 'Accede a tu información y análisis en cualquier momento, desde donde estés'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      titulo: 'Máxima Seguridad',
      descripcion: 'Tu privacidad es nuestra prioridad, con los más altos estándares de protección'
    }
  ];

  const testimonios = [
    {
      nombre: "María González",
      rol: "Paciente",
      testimonio: "Esta plataforma cambió mi vida. El análisis preliminar me ayudó a tomar decisiones informadas sobre mi salud.",
      avatar: "/images/avatar1.svg"
    },
    {
      nombre: "Dr. Carlos Ruiz",
      rol: "Médico",
      testimonio: "Una herramienta innovadora que complementa perfectamente la atención médica tradicional.",
      avatar: "/images/avatar2.svg"
    },
    {
      nombre: "Ana Martínez",
      rol: "Paciente",
      testimonio: "La facilidad de uso y la precisión de los análisis me dan tranquilidad. ¡Altamente recomendado!",
      avatar: "/images/avatar3.svg"
    }
  ];

  const estadisticas = [
    {
      numero: "98%",
      texto: "Satisfacción de usuarios"
    },
    {
      numero: "24/7",
      texto: "Disponibilidad"
    },
    {
      numero: "+10k",
      texto: "Usuarios activos"
    },
    {
      numero: "95%",
      texto: "Precisión en análisis"
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: { xs: 8, md: 15 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("/images/pattern.svg")',
            opacity: 0.1,
            zIndex: 1
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  color: '#fff',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Tu Salud en las Mejores Manos
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6 }}
              >
                Descubre el futuro de la atención médica con nuestra innovadora plataforma de análisis inteligente
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/registro')}
                  sx={{
                    backgroundColor: 'white',
                    color: theme.palette.primary.main,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  Regístrate
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Iniciar Sesión
                </Button>
              </Box>
              {/* Características Destacadas */}
              <Box sx={{ mt: 4, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {['Análisis Instantáneo', 'Respaldo Médico', 'Fácil de Usar'].map((feature, index) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} key={feature}>
                    <CheckCircleIcon sx={{ color: 'success.light' }} />
                    <Typography variant="body1">{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              {!heroImageLoaded ? (
                <Skeleton 
                  variant="rectangular" 
                  width="100%" 
                  height={400}
                  sx={{ 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)'
                  }}
                />
              ) : (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 2,
                    transition: { duration: 0.3 }
                  }}
                >
                  <Box
                    component="img"
                    src="/images/hero-medical.svg"
                    alt="Salud Digital"
                    className={`hero-image ${heroImageLoaded ? 'loaded' : ''}`}
                    sx={{
                      width: '100%',
                      maxWidth: 600,
                      height: 'auto',
                      filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.2))'
                    }}
                  />
                </motion.div>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Estadísticas */}
      <Box>
        <Paper elevation={3} sx={{ py: 4, px: 2, borderRadius: 4 }}>
          <Grid container spacing={2}>
            {estadisticas.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Typography variant="h3" component="div" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                  {stat.numero}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {stat.texto}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>

      {/* Beneficios */}
      <Box>
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Grid container spacing={4}>
            {beneficios.map((beneficio, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 4 }}>
                    <Box 
                      sx={{ 
                        mb: 3,
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        backgroundColor: `${theme.palette.primary.main}10`
                      }}
                    >
                      {beneficio.icon}
                    </Box>
                    <Typography
                      gutterBottom
                      variant="h5"
                      component="h3"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      {beneficio.titulo}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {beneficio.descripcion}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonios */}
      <Box sx={{ bgcolor: 'grey.50', py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {testimonios.map((testimonio, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'visible'
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ position: 'absolute', top: -20, left: 20 }}>
                      <FormatQuoteIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body1" sx={{ mt: 3, mb: 4, lineHeight: 1.8 }}>
                      "{testimonio.testimonio}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={testimonio.avatar} sx={{ width: 56, height: 56 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {testimonio.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonio.rol}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          py: 10,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("/images/pattern.svg")',
            opacity: 0.1
          }
        }}
      >
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{ mb: 3, fontWeight: 700 }}
          >
            Comienza tu viaje hacia una mejor salud
          </Typography>
          <Typography
            variant="h6"
            sx={{ mb: 6, opacity: 0.9, maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}
          >
            Únete a miles de personas que ya han descubierto una nueva forma de cuidar su salud. Tu bienestar es nuestra prioridad.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/registro')}
            sx={{
              backgroundColor: 'white',
              color: theme.palette.primary.main,
              px: 6,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                transform: 'translateY(-2px)',
                boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Regístrate
          </Button>
        </Container>
      </Box>

      {/* Footer con Enlaces Legales */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          py: 4,
          mt: 8,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid item>
              <Button
                color="inherit"
                onClick={() => navigate('/terminos-condiciones')}
                sx={{ textTransform: 'none' }}
              >
                Términos y Condiciones
              </Button>
            </Grid>
            <Grid item>
              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            </Grid>
            <Grid item>
              <Button
                color="inherit"
                onClick={() => navigate('/privacidad')}
                sx={{ textTransform: 'none' }}
              >
                Política de Privacidad
              </Button>
            </Grid>
          </Grid>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            © {new Date().getFullYear()} Symtomatix. Todos los derechos reservados.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 